import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * AIDialog — reusable in-house modal primitive with the same a11y
 * contract as ExplainFindingDialog: Enter/Space on trigger opens,
 * Escape closes and restores focus, Tab is trapped inside the panel,
 * `role="dialog"` + `aria-modal="true"`, click-on-backdrop dismisses.
 *
 * Consumers pass `renderTrigger`, `renderHeader`, and `renderBody`
 * plus a `testIdPrefix` so every internal element has a stable
 * data-testid.
 *
 * Contract with callers:
 *   * `onOpenChange(true)` is called before the dialog appears;
 *     consumers use this to fire their fetch. `onOpenChange(false)`
 *     is called after the dialog dismisses.
 *   * The a11y contract is invariant — the caller cannot disable it.
 *     If focus management is broken, the fix goes here.
 */
export function AIDialog({
  testIdPrefix,
  ariaLabel,
  renderTrigger,
  renderHeader,
  renderBody,
  renderFooter,
  onOpenChange,
}) {
  const [open, setOpen] = useState(false);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const previousFocusRef = useRef(null);

  const openPanel = useCallback(() => {
    previousFocusRef.current = document.activeElement;
    setOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange]);

  const closePanel = useCallback(() => {
    setOpen(false);
    const prev = previousFocusRef.current;
    if (prev && typeof prev.focus === "function") {
      prev.focus();
    } else if (triggerRef.current) {
      triggerRef.current.focus();
    }
    onOpenChange?.(false);
  }, [onOpenChange]);

  const onTriggerKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openPanel();
      }
    },
    [openPanel],
  );

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closePanel();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll(
          'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, closePanel]);

  useEffect(() => {
    if (open && closeRef.current) {
      const id = window.setTimeout(() => closeRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  return (
    <>
      {renderTrigger({
        triggerRef,
        onClick: openPanel,
        onKeyDown: onTriggerKeyDown,
        ariaExpanded: open,
      })}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          data-testid={`${testIdPrefix}-dialog`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePanel();
          }}
        >
          <div
            ref={panelRef}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-full max-w-2xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
              {renderHeader?.()}
              <button
                ref={closeRef}
                type="button"
                onClick={closePanel}
                data-testid={`${testIdPrefix}-close`}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                aria-label="Close AI dialog"
              >
                <CloseIcon />
              </button>
            </header>
            <div className="max-h-[70vh] overflow-y-auto px-4 py-4">
              {renderBody?.()}
            </div>
            {renderFooter && (
              <footer className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[10px] text-slate-500">
                {renderFooter()}
              </footer>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12" />
      <path d="M6 18L18 6" />
    </svg>
  );
}
